import { useEffect, type ReactNode } from "react";
import { router } from "@inertiajs/react";
import { User, Shield, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { NavLink, useCurrentPath } from "@/lib/nav";

interface NavItem {
  to: string;
  label: string;
  description?: string;
  icon: LucideIcon;
}

const groups: { label: string; items: NavItem[] }[] = [
  {
    label: "Personal",
    items: [
      { to: "/account", label: "Profile", icon: User, description: "Name, email and avatar" },
      { to: "/account/password", label: "Security", icon: Shield, description: "Update your password" },
    ],
  },
];

const FLAT_ITEMS = groups.flatMap((g) => g.items);

interface AccountLayoutProps {
  /** The current section's label — shown in the page header. */
  title: string;
  /** Optional one-line description; falls back to the active section's description. */
  description?: ReactNode;
  children: ReactNode;
}

export function AccountLayout({ title, description, children }: AccountLayoutProps) {
  const path = useCurrentPath();

  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · stabiliti`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  const fallbackDescription = FLAT_ITEMS.find((i) => i.to === path)?.description;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-border-subtle bg-canvas px-4 pt-4 pb-3 sm:px-6 sm:pt-5 sm:pb-4">
        <div className="text-xs text-fg-subtle">Account</div>
        <h1 className="mt-1 text-xl font-semibold tracking-tight text-fg">{title}</h1>
        {(description ?? fallbackDescription) && (
          <p className="mt-1 text-sm text-fg-muted">{description ?? fallbackDescription}</p>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
        <nav
          className="hidden w-[240px] shrink-0 overflow-y-auto border-r border-border-subtle bg-subtle/30 px-3 py-4 lg:block"
          aria-label="Account sections"
        >
          {groups.map((g) => (
            <div key={g.label} className="mb-4">
              <div className="px-2 pb-1 text-[10px] font-medium uppercase tracking-wider text-fg-subtle">
                {g.label}
              </div>
              <ul className="flex flex-col gap-0.5">
                {g.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end
                      className={({ isActive }) =>
                        cn(
                          "flex items-start gap-2.5 rounded-[var(--radius-md)] px-2 py-1.5 text-[13px]",
                          "transition-[background-color,color] duration-[var(--duration-micro)]",
                          isActive
                            ? "bg-accent-subtle text-accent-text"
                            : "text-fg-muted hover:bg-surface-hover hover:text-fg",
                        )
                      }
                    >
                      <item.icon className="mt-0.5 size-3.5 shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium">{item.label}</span>
                        {item.description && (
                          <span className="block text-[11.5px] text-fg-subtle">{item.description}</span>
                        )}
                      </span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-b border-border-subtle bg-canvas px-4 py-2 lg:hidden">
          <select
            value={path}
            onChange={(e) => router.visit(e.target.value)}
            className="h-9 w-full rounded-[var(--radius-md)] border border-border-default bg-surface px-2.5 text-sm text-fg"
          >
            {FLAT_ITEMS.map((item) => (
              <option key={item.to} value={item.to}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <main className="flex min-h-0 flex-1 flex-col overflow-auto">
          <div className="mx-auto w-full max-w-[840px] px-4 py-5 sm:px-6 sm:py-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
